import React, { useEffect, useState } from 'react';
import { Input, InputNumber, SelectPicker } from 'rsuite';
import DatePicker from '../shared/DatePicker';
import { Button, ConfirmModal, Modal } from '@nsi-advisory-services/components';

import { useAddPatoAdjustment, useEmployeePato, useVoidPatoAdjustment } from '../../hooks/useQueries';
import { showToast } from '../../utils/toast';

// One employee's PATO (Paid Any Time Off) balance for a year.
//
// The balance is a ledger the server works out (see buildPatoLedger): the
// company grant in force on 1 January, every adjustment made to it, and the
// time booked against the PATO tasks in TimeCamp. Nothing here stores a total.
//
// Adjustments are individual, dated items - a credit or a debit with a reason -
// rather than one figure for the year, because each one has to be able to
// stand on its own record. They are never edited: a wrong one is voided and the
// right one added, so the ledger shows both.
//
// Under unlimited time off there is no allowance and nothing remaining; what
// has been used is still shown, because it is still recorded.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.

// Where paystubs actually live. The note below tells somebody to check theirs
// against this balance, so the page hands them the door rather than the address.
const PAYCHECKS_URL = 'https://workforce.intuit.com/app/payroll-employee-portal-ui/portal/paychecks';

const parseDateOnly = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

// The picked day as a date-only string, from its local parts - toISOString
// would move it to the day before for anyone east of Greenwich after midnight UTC.
const toDateOnly = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// A pay period as a person would say it - "Sep 1 to Sep 15". Parsed off the
// date-only strings rather than through new Date(iso), which would read them as
// UTC midnight and show the day before to anyone west of Greenwich.
const formatPeriod = (period) => {
  const start = parseDateOnly(period?.start);
  const end = parseDateOnly(period?.end);
  if (!start || !end) return '';
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} to ${fmt(end)}`;
};

const formatDate = (iso) => {
  const d = parseDateOnly(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};

const formatHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
};

const formatSigned = (value) => {
  const n = Number(value) || 0;
  return `${n > 0 ? '+' : '−'}${formatHours(Math.abs(n))}`;
};

// Which years are offerable: last year through next, which covers correcting a
// closed year and setting up the next one.
const yearOptions = () => {
  const now = new Date().getFullYear();
  return [now - 1, now, now + 1].map((y) => ({ label: String(y), value: y }));
};

// The day a new adjustment defaults to: today in the current year, otherwise
// 1 January of the year being looked at, so it lands in the year on screen.
const defaultAdjustmentDate = (year) => (
  year === new Date().getFullYear() ? new Date() : new Date(year, 0, 1)
);

function AddAdjustmentModal({ open, onClose, employeeId, year }) {
  const add = useAddPatoAdjustment();
  const [date, setDate] = useState(() => defaultAdjustmentDate(year));
  const [hours, setHours] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  // Reopening starts clean rather than resuming one the user has forgotten.
  useEffect(() => {
    if (!open) return;
    setDate(defaultAdjustmentDate(year));
    setHours(null);
    setReason('');
    setError(null);
  }, [open, year]);

  const handleSave = async () => {
    const value = Number(hours);
    const iso = toDateOnly(date);
    if (!iso) return setError('Pick the date the adjustment takes effect.');
    if (!Number.isFinite(value) || value === 0) {
      return setError('Enter the hours - positive to credit, negative to debit.');
    }
    if (!reason.trim()) return setError('Every adjustment needs a reason.');
    setError(null);
    try {
      await add.mutateAsync({ employeeId, date: iso, hours: value, reason: reason.trim() });
      showToast('success', `PATO adjustment of ${formatSigned(value)} hours added for ${formatDate(iso)}.`);
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not add the adjustment.');
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="xs" backdrop="static">
      <Modal.Header>
        <Modal.Title>Add PATO adjustment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? <div className="me-retirement__error">{error}</div> : null}

        <div className="me-pato__form">
          <label className="me-pato__formfield">
            <span className="me-field__label">Effective date</span>
            <DatePicker
              value={date}
              onChange={setDate}
              oneTap
              cleanable={false}
              format="MM/dd/yyyy"
              block
            />
          </label>

          <label className="me-pato__formfield">
            <span className="me-field__label">Hours</span>
            <InputNumber
              value={hours}
              onChange={setHours}
              onFocus={(event) => event.target.select()}
              step={1}
              postfix="hours"
              placeholder="e.g. 8 or -4"
            />
            <span className="me-pato__hint">Positive credits the balance; negative debits it.</span>
          </label>

          <label className="me-pato__formfield">
            <span className="me-field__label">Reason</span>
            <Input
              as="textarea"
              rows={2}
              value={reason}
              onChange={setReason}
              maxLength={500}
              placeholder="What this adjustment is for"
            />
          </label>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} disabled={add.isPending}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} loading={add.isPending}>Add adjustment</Button>
      </Modal.Footer>
    </Modal>
  );
}

// The Used and Remaining cards for a year's balance (`stored` is useEmployeePato's data).
// The allowance is what was credited; these are what it has come to. They are typeset
// identically and sized alike - they are one pair read together. Colour still carries
// the one distinction worth making: red once it goes negative.
export function PatoUsageCards({ stored }) {
  const unlimited = Boolean(stored?.unlimited);
  // Only worth splitting when the period in progress actually holds something.
  const splitUsed = Number(stored?.usedThisPeriodHours) > 0
    && stored?.usedPreviousHours != null;

  return (
    <div className="me-pato__usage">
      {/* One Used figure normally, two once something has been booked in
          the period being worked.

          The split only earns its place when there is something in it.
          Showing "this period: 0.00" on every quiet fortnight would be two
          pills saying what one already said, and would bury the distinction
          on the fortnight it finally matters. */}
      {splitUsed ? (
        <>
          <div className="me-pato__usageitem">
            <span className="me-pato__usagelabel">Used prev</span>
            <span className="me-pato__usagevalue bdc-num">
              {formatHours(stored.usedPreviousHours)} hrs
            </span>
          </div>
          <div className="me-pato__usageitem">
            <span className="me-pato__usagelabel">Used this period</span>
            <span className="me-pato__usagevalue bdc-num">
              {formatHours(stored.usedThisPeriodHours)} hrs
            </span>
          </div>
        </>
      ) : (
        <div className="me-pato__usageitem">
          <span className="me-pato__usagelabel">Used</span>
          <span className="me-pato__usagevalue bdc-num">
            {stored?.usedHours == null ? '—' : `${formatHours(stored.usedHours)} hrs`}
          </span>
        </div>
      )}
      {unlimited ? null : (
        <div className="me-pato__usageitem">
          <span className="me-pato__usagelabel">Remaining</span>
          <span
            className={`me-pato__usagevalue bdc-num${
              stored?.remainingHours != null && stored.remainingHours < 0 ? ' bdc-text-bad' : ''
            }`}
          >
            {stored?.remainingHours == null ? '—' : `${formatHours(stored.remainingHours)} hrs`}
          </span>
        </div>
      )}
    </div>
  );
}

// This year's Used and Remaining cards on their own, for showing outside the full
// balance (the closed PATO and Retirement section on a profile). Same query as
// PatoBalance's default year, so it adds no request.
export function PatoUsageSummary({ employeeId }) {
  const query = useEmployeePato(employeeId, new Date().getFullYear(), { enabled: Boolean(employeeId) });
  return query.data ? <PatoUsageCards stored={query.data} /> : null;
}

export default function PatoBalance({ employeeId, canManage }) {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [isAdding, setIsAdding] = useState(false);
  const [voiding, setVoiding] = useState(null);

  const query = useEmployeePato(employeeId, year, { enabled: Boolean(employeeId) });
  const voidAdjustment = useVoidPatoAdjustment();
  const stored = query.data;

  const confirmVoid = async () => {
    if (!voiding) return;
    try {
      await voidAdjustment.mutateAsync({ employeeId, adjustmentId: voiding.id });
      showToast('success', 'PATO adjustment voided.');
      setVoiding(null);
    } catch (err) {
      showToast('error', err?.message || 'Could not void the adjustment.');
    }
  };

  if (query.isLoading) {
    return <div className="me-retirement__loading">Loading PATO balance&hellip;</div>;
  }

  if (query.isError) {
    return (
      <div className="me-retirement__error">
        PATO balances are unavailable: {query.error?.message || 'the request failed.'}
      </div>
    );
  }

  const unlimited = Boolean(stored?.unlimited);
  const adjustments = stored?.adjustments || [];
  const adjustmentHours = Number(stored?.adjustmentHours) || 0;
  // Matches PatoUsageCards: the period note below only shows when the cards split Used.
  const splitUsed = Number(stored?.usedThisPeriodHours) > 0
    && stored?.usedPreviousHours != null;

  return (
    <div className="me-pato">
      <div className="me-retirement__head">
        <div className="me-field__label">PATO balance</div>
        <div className="me-pato__controls">
          <SelectPicker
            data={yearOptions()}
            value={year}
            onChange={(next) => setYear(next)}
            cleanable={false}
            searchable={false}
            size="sm"
            style={{ width: 100 }}
          />
          {canManage ? (
            <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => setIsAdding(true)}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              Add adjustment
            </button>
          ) : null}
        </div>
      </div>

      <div className="me-pato__value">
        {unlimited ? (
          <>
            <span className="me-pato__hours">Unlimited</span>
            <span className="me-pato__unit">time off for {year}</span>
          </>
        ) : (
          <>
            <span className="me-pato__hours bdc-num">{formatHours(stored?.allowanceHours)}</span>
            <span className="me-pato__unit">hours for {year}</span>
            {/* How the allowance is made up, once it is more than the grant.
                The grant alone needs no sum. */}
            {adjustmentHours !== 0 && stored?.beginningHours != null ? (
              <span className="me-pato__tag">
                {formatHours(stored.beginningHours)} granted {formatSigned(adjustmentHours)} adjusted
              </span>
            ) : null}
          </>
        )}
      </div>

      <PatoUsageCards stored={stored} />

      {/* Each adjustment on its own line, as the ledger records it. Shown to
          the employee too: it is their balance, and a figure that moved for a
          reason they cannot see is the one they will ask about. */}
      {adjustments.length > 0 ? (
        <div className="me-pato__adjustments">
          <div className="me-pato__usagelabel">Adjustments in {year}</div>
          <ul className="me-pato__adjlist">
            {adjustments.map((adjustment) => (
              <li key={adjustment.id} className="me-pato__adj">
                <span className="me-pato__adjdate bdc-num">{formatDate(adjustment.date)}</span>
                <span
                  className={`me-pato__adjhours bdc-num${adjustment.hours < 0 ? ' bdc-text-bad' : ''}`}
                >
                  {formatSigned(adjustment.hours)}
                </span>
                <span className="me-pato__adjreason">
                  {adjustment.reason}
                  {adjustment.pending ? <span className="me-pato__tag">Not yet in effect</span> : null}
                  {adjustment.createdByName ? (
                    <span className="me-pato__adjby">by {adjustment.createdByName}</span>
                  ) : null}
                </span>
                {canManage ? (
                  <button
                    type="button"
                    className="bdc-btn bdc-btn--sm me-pato__adjvoid"
                    onClick={() => setVoiding(adjustment)}
                    aria-label={`Void the adjustment of ${formatSigned(adjustment.hours)} hours on ${formatDate(adjustment.date)}`}
                  >
                    Void
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* The caveat matters more than the figure does.

          This is a live read of the timesheet, which is not the same thing as a
          settled balance: time can still be entered, corrected or moved for the
          period in progress, and none of it is final until payroll has run
          against it. Somebody planning time off on a number this page gave them
          should know it can still move, and know where the authoritative
          version is. */}
      <div className="me-retirement__note">
        {unlimited
          ? 'Time off is unlimited under the current company policy. Time booked against the time-off and sick time '
            + 'tasks is still recorded, so Used is shown, but nothing is drawn down.'
          : stored?.usedHours == null
            ? 'Time booked against the time-off and sick time tasks draws this down. What has been taken could not be '
              + 'read from TimeCamp just now, so only the allowance is shown.'
            : 'Time booked against the time-off and sick time tasks draws this down. It is only accurate as of the end '
              + 'of the current pay period, once payroll has been run for it - check your balance against your '
              + 'paystub to be sure.'}
        {splitUsed && stored?.currentPeriod ? (
          <>
            {' '}
            This period runs {formatPeriod(stored.currentPeriod)}, and the hours in it are the ones still
            able to change.
          </>
        ) : null}
      </div>

      {/* The note tells somebody to check their paystub; this is the paystub.
          Offered whatever the usage figure did: if the balance could not be
          read at all, going and looking is more use, not less. */}
      <a
        className="bdc-btn bdc-btn--sm me-pato__paystub"
        href={PAYCHECKS_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
        Check paystub in QuickBooks
      </a>

      {canManage ? (
        <>
          <AddAdjustmentModal
            open={isAdding}
            onClose={() => setIsAdding(false)}
            employeeId={employeeId}
            year={year}
          />

          <ConfirmModal
            open={Boolean(voiding)}
            title="Void PATO adjustment"
            body={voiding ? (
              <>
                Void the adjustment of <strong>{formatSigned(voiding.hours)} hours</strong> on{' '}
                {formatDate(voiding.date)}? It stops counting towards the balance. The record is kept, and
                a corrected adjustment can be added in its place.
              </>
            ) : null}
            confirmText="Void"
            danger
            loading={voidAdjustment.isPending}
            onConfirm={confirmVoid}
            onCancel={() => setVoiding(null)}
          />
        </>
      ) : null}
    </div>
  );
}
